/**
 * HOW WORDS FIND THEIR SIZE.
 *
 * The old answer was a ladder: seven point sizes, tried largest first,
 * the first one that fitted won — and it was chosen ONCE, when the scoops
 * came to rest. Everything in between was that stale choice stretched by
 * a live scale factor, so the moment the venn stopped moving the words
 * re-wrapped and jumped to their real size. Mathew, at the bench: "is not
 * pretty".
 *
 * The new answer has no ladder and no settling moment. A label is set
 * ONCE per text, in every way it could reasonably break: as one line, as
 * two, as three. Those TYPESETS are pure typography — the words, their
 * widths, the shape of the block — and they do not depend on the venn at
 * all, so they never need recomputing while it moves.
 *
 * Then, every frame, the drawing asks each typeset one arithmetic
 * question — "how big could you be drawn in the room I have right now?" —
 * and shows the biggest answer. The size that results is CONTINUOUS in
 * the budget, so a region that breathes has words that breathe with it,
 * and there is no settle to jump at because nothing waits for one.
 *
 * The one discrete event left is a change of TYPESET, and at the exact
 * moment two typesets tie they are by definition the same size — so even
 * that is a change of line breaks, never a change of scale. The drawing
 * dissolves between them, and holds off switching until the challenger is
 * plainly better (see `SWITCH_MARGIN`). Zen over twitch.
 *
 * Everything here is pure and measured through an injected `measure`, so
 * it can be tested without a font engine — and so the drawing and the
 * editor's invisible ghost cannot reach different answers: they call the
 * same functions, INCLUDING the chooser. That last word is load-bearing
 * and was not always true. The deadband lived in the drawing and the
 * ghost called `biggestFit` raw, so the claim above was false in exactly
 * the case it mattered: a tap into a region whose venn had drifted. It is
 * `chooseTypeset` now, and both callers go through it.
 *
 * WHY IT LIVES IN CORE (moved from `app/src/labelFit.ts`, issues 230/300).
 * The survey that catalogued app-side pure logic measured this file at
 * ZERO imports — not "few", none: it is arithmetic over numbers a caller
 * hands it, with the one measurement it needs injected. Nothing in it was
 * ever about React, Skia, or a device. Two callers now want the same
 * answers, on two sides of a wall that cannot share app code: the drawing
 * (LabelsLayer, through Skia's own glyph advances) and `render.ts`'s still
 * picture (through a table of the shipped face's advances). A shared venn
 * opened in a browser used to arrive as a smear because the second caller
 * had no fit at all. One rule, one file, both sides of the seam.
 *
 * CORE OWNS THE LAYOUT; THE TWO CALLERS ARE SKINS. That is the whole
 * architectural point of the move and it is worth being precise about,
 * because "shared" is easy to claim and easy to hollow out. Everything
 * that DECIDES anything lives here and has exactly one implementation:
 *
 *   `typesetsFor`  the candidate line breakings, k²-exhaustive
 *   `extentsOf` / `extentsAt`  those candidates as a flat table, at the
 *                  face's own leading or at a caller's
 *   `fitScale`     the ellipse rule against a region's budget
 *   `drawnSize`    how big a block comes out, capped at the caller's base
 *   `biggestFit`   which breaking wins — the ONE comparison loop
 *   `chooseTypeset`  that, plus the deadband
 *   `floorFade`    how plainly a size below the floor can be read
 *
 * `LabelsLayer` and `renderVennSvg` both call into that list and neither
 * contains fitting arithmetic of its own. The viewer used to drift from
 * the app because it was a second layout implementation — it had none of
 * this and set every label at a fixed size. What keeps that from growing
 * back in these two files is `guards/scan.ts`'s `layoutSkinViolations`,
 * which trips on the natural forms of a re-derived ellipse rule. It
 * guards the SHAPE, not every path: drift could still arrive by a
 * re-derivation disguised past a source regex, or through a THIRD file
 * that lays labels out and was never added to the guard's list. Those
 * are review's to catch — the guard backstops the common slip, it does
 * not close the door by itself. (The guard's own comment is honest about
 * this; so is this sentence, because the first draft of it — "it cannot
 * drift now without someone deleting a call" — claimed a completeness
 * neither the guard nor anything else here has.)
 *
 * WHAT IS GENUINELY FORKED, named rather than smoothed over, because a
 * claim of "one function, two inputs" that quietly hides four differences
 * is worse than an honest list:
 *
 *  1. THE MEASURER. The app hands `typesetsFor` Skia's advances off the
 *     live face; the renderer hands it `textWidth.ts`'s static table.
 *     Unavoidable and by design — that seam is the injected `measure`.
 *  2. THE DEADBAND. `chooseTypeset` is the app's; a still picture has no
 *     incumbent to be loyal to and no frame rate to twitch at, so the
 *     renderer calls `biggestFit` directly. Not a fork in the rule, a
 *     fork in whether the rule has any history to consult.
 *  3. THE LEADING. The app takes the face's designed line box (1.602 em
 *     for Baloo — a Devanagari-sized box); the renderer sets 1.2 against
 *     measured Latin ink. `extentsAt` exists so that difference is a
 *     PARAMETER rather than a second implementation. See its comment.
 *  4. BELOW THE FLOOR. The app fades (`floorFade`) because the region
 *     will have room again in a moment; the renderer clamps, because a
 *     shared picture has no next moment and an invisible label in the one
 *     image a recipient sees is worse than a small one.
 *
 * And two differences that are decisions about pixels rather than about
 * layout at all, filed as issue 305: the app caps at BASE_SIZE = 28 and
 * draws SemiBold, the renderer caps at 22 and resolves Regular.
 */

/** One drawn line, its width measured at `BASE_SIZE`. */
export interface Line {
  text: string;
  width: number;
}

/**
 * One way of setting a label: the lines, and the block they make. Every
 * number is in BASE_SIZE units — the drawing scales the whole group, so
 * one measurement basis serves every size.
 */
export interface Typeset {
  lines: Line[];
  /** widest line */
  blockW: number;
  /** lines * lineHeight */
  blockH: number;
  lineHeight: number;
  ascent: number;
  /**
   * Half of the leading that LINE_SPACING adds on top of the face's own
   * ascent+descent. Every text engine — CSS, and UIKit under React
   * Native — splits that extra space evenly above and below each line, so
   * the first baseline sits half a leading BELOW the top of the block. The
   * drawing has to do the same or the invisible input's caret and
   * selection, which are laid out by those engines, come out offset from
   * the glyphs Skia painted.
   *
   * READ BY the editor's ghost (LabelEditor's box `marginTop`), which
   * lifts itself by exactly this. It sits on the ghost's side of the seam
   * on purpose: correcting the DRAWING instead would move every painted
   * label by about a pixel and rebuild every screenshot baseline, to fix
   * something only the caret can see.
   */
  halfLeading: number;
}

/** A face's vertical metrics, both positive, in BASE_SIZE units. */
export interface FaceMetrics {
  ascent: number;
  descent: number;
}

/**
 * The size everything is measured at, and the ceiling no label passes.
 *
 * A label can be smaller than this — the room decides — but never larger:
 * 28pt is as big as a word gets in a venn this size before it stops being
 * a caption and starts being a poster.
 */
export const BASE_SIZE = 28;

/** Words may break this far and no further. */
export const MAX_LINES = 3;

/** Leading, as a multiple of the face's own ascent + descent. */
export const LINE_SPACING = 1.1;

/**
 * The legibility cliff. 10pt is about as small as a label can go on a
 * phone screen and still be read rather than merely seen; below it the
 * words fade rather than shrinking on.
 */
export const FLOOR_SIZE = 10;

/** Points of further shrink over which that fade completes. */
export const FADE_RANGE_PT = 4;

/**
 * How much better a rival typeset has to be before the drawing changes
 * its mind: it must offer this much MORE drawn size than the one already
 * showing.
 *
 * Mathew's ruling, twice, in his own words: "Zen over Twitch" and then
 * "Never twitch." So this is deliberately wide. At the point where two
 * typesets tie, either would draw at the same size; without a margin, a
 * region breathing across that tie would flicker between two line
 * breakings at frame rate. At 18% the region has to make a real,
 * committed move before the words re-break — and because the switch is a
 * dissolve rather than a cut, the cost of being late is nothing at all.
 */
export const SWITCH_MARGIN = 0.18;

/**
 * How much of its budget a block of text is allowed to claim.
 *
 * The budget box is NOT a true inscribed rectangle — core's own docstring
 * says so: its height is measured at the anchor's exact axis offset, so
 * the box's corners poke past the bounding curve even when its centre line
 * fits. Treating the budget as an inscribed ELLIPSE instead is the
 * conservative reading core asks for: a block fits when its own corner
 * lands inside that ellipse, i.e. (w/W)² + (h/H)² <= 1.
 */
export function fitScale(
  blockW: number,
  blockH: number,
  budgetW: number,
  budgetH: number,
): number {
  'worklet';
  if (budgetW <= 0 || budgetH <= 0) return 0;
  const reach = Math.hypot(blockW / budgetW, blockH / budgetH);
  if (reach <= 0) return 0;
  return Math.min(1, 1 / reach);
}

/**
 * How big a typeset would be DRAWN, in points, in the room it is given.
 *
 * The whole live decision reduces to this one number, which is why it is
 * arithmetic and not measurement: the block's extents are known, the
 * budget is known, and the answer is what the ellipse rule allows, capped
 * at the base size. An empty typeset — a slot that has no such breaking —
 * is worth nothing, and says so.
 *
 * `base` IS THE CALLER'S OWN CEILING, and it is a parameter rather than
 * this module's constant because there are two callers with two ceilings
 * and only one rule. The app's canvas caps a label at BASE_SIZE = 28 (the
 * default, so every existing call site reads exactly as it did);
 * `render.ts` caps the still picture at its own LABEL_FONT_SIZE. Both must
 * be told the same thing about how big a block CAN be drawn — they simply
 * disagree about how big it is ever ALLOWED to be, which is a decision
 * about pixels and not about fitting. The block extents passed in must of
 * course be measured at the same `base`.
 */
export function drawnSize(
  blockW: number,
  blockH: number,
  budgetW: number,
  budgetH: number,
  base: number = BASE_SIZE,
): number {
  'worklet';
  if (blockH <= 0) return 0;
  return base * fitScale(blockW, blockH, budgetW, budgetH);
}

/**
 * Which typeset would come out biggest in this budget, given the flat
 * `[w0, h0, w1, h1, …]` table the drawing carries on the UI thread.
 *
 * -1 when there is nothing to draw at all.
 */
export function biggestFit(
  extents: readonly number[],
  budgetW: number,
  budgetH: number,
): number {
  'worklet';
  let best = -1;
  let bestSize = 0;
  for (let index = 0; index * 2 + 1 < extents.length; index++) {
    const size = drawnSize(
      extents[index * 2]!,
      extents[index * 2 + 1]!,
      budgetW,
      budgetH,
    );
    if (size > bestSize) {
      bestSize = size;
      best = index;
    }
  }
  return best;
}

/**
 * WHICH TYPESET TO SHOW, deadband and all. The one chooser.
 *
 * `shown` is the index already on show, or -1 for "no opinion yet" — a
 * fresh label, or one whose text has just changed, which rebuilds the
 * table and makes any old index meaningless.
 *
 * This exists as a function because it was written twice. The drawing
 * applied the deadband and the editor's invisible ghost called
 * `biggestFit` raw, so a tap into a region whose venn had drifted since
 * the words were last re-broken could lay the caret out over one line
 * breaking while Skia painted another — under a file whose own docstring
 * swore the two "cannot possibly reach different answers". One rule, one
 * function, both callers.
 *
 * A rival must beat the incumbent by `SWITCH_MARGIN` of DRAWN SIZE, not
 * merely beat it: at the tie point the two draw identically, so a region
 * breathing across the tie would re-break the words at frame rate.
 */
export function chooseTypeset(
  shown: number,
  extents: readonly number[],
  budgetW: number,
  budgetH: number,
): number {
  'worklet';
  const best = biggestFit(extents, budgetW, budgetH);
  if (best < 0) return shown;
  // No incumbent, or one that no longer indexes anything: take the best
  // outright. There is nothing to be loyal to.
  if (shown < 0 || shown * 2 + 1 >= extents.length) return best;
  if (best === shown) return shown;
  const mine = drawnSize(
    extents[shown * 2]!,
    extents[shown * 2 + 1]!,
    budgetW,
    budgetH,
  );
  const rival = drawnSize(
    extents[best * 2]!,
    extents[best * 2 + 1]!,
    budgetW,
    budgetH,
  );
  return rival > mine * (1 + SWITCH_MARGIN) ? best : shown;
}

/**
 * THE ONE WHITESPACE VOCABULARY — what the drawing breaks at, what the
 * ghost may write a break into, and what the keyboard flattens.
 *
 * It exists because three places were each asking a slightly different
 * question with `/\s+/` and getting three different answers, and the
 * disagreements were all invisible until Severino went looking (2026-08-25).
 *
 * BREAKING is JS's `\s` with three characters TAKEN OUT and one PUT IN:
 *
 *  - OUT: U+00A0 NBSP, U+202F NARROW NBSP, U+FEFF ZWNBSP, and U+2007 FIGURE
 *    SPACE. JS's `\s` matches the first three, so `text.split(/\s+/)` broke
 *    "100 km" into two words at a space whose entire meaning is DO NOT BREAK
 *    HERE. The drawing wrapped there, and `breakAt` then wrote a hard
 *    newline over the very character asking it not to. They are word glue
 *    now, and a run containing one is one word.
 *
 *  - IN: U+0085 NEL. It is NOT in `\s`, so the tokenizer glued "ab<NEL>cd"
 *    into a single word — while both text engines break a line at it.
 *    Measured: Chromium laid two lines in a one-line box (scrollHeight 99
 *    against clientHeight 49) and so did CoreText on iOS 26.3. An extra
 *    line INSIDE a word the ghost had been told was unbreakable is a line
 *    `breakAt` cannot pin, so the character has to go before it arrives.
 *
 * EVERY BREAKING CHARACTER PAINTS AS ONE PLAIN SPACE, because `typesetsFor`
 * splits on runs of them and rejoins words with U+0020. That is the whole
 * reason the ghost may flatten them: it is not discarding anything the
 * visitor can see, it is agreeing with the picture.
 */
const BREAKING_SOURCE =
  '[\t\n\v\f\r \u0085\u1680\u2000-\u2006\u2008-\u200a\u2028\u2029\u205f\u3000]';

/**
 * ONE SOURCE, THREE READINGS, and the negation is DERIVED rather than
 * retyped. A second copy of this character list would be a second list to
 * forget to update, and the whole defect class this vocabulary closes was
 * three places asking the same question differently.
 *
 * THE CONTROL CHARACTERS ARE THE POINT — a vertical tab and a form feed
 * are exactly what a text engine breaks a line at, and exactly what must
 * never reach the ghost. Written as a regex LITERAL this would trip
 * `no-control-regex`, correctly, since that rule exists to catch the ones
 * nobody meant. Building from a shared string puts them past its analysis,
 * which is a side effect of having one source and not a way around the
 * rule: if this list is ever inlined back into a literal, the rule will
 * fire and the answer is to keep the shared source, not to silence it.
 */
export const BREAKING_WS = new RegExp(BREAKING_SOURCE, 'g');

/** A run of them — the tokenizer's own splitter. */
const BREAKING_RUN = new RegExp(BREAKING_SOURCE + '+');

/** A word: anything that is NOT breaking whitespace, glue included. */
const WORD_RUN = new RegExp('[^' + BREAKING_SOURCE.slice(1, -1) + ']+', 'g');

/**
 * Every breaking character flattened to a plain space, ONE FOR ONE.
 *
 * Length-preserving on purpose: this runs on the keyboard path, where the
 * result becomes the draft and every caret offset in the editor has to keep
 * meaning what it meant. Non-breaking glue is not touched.
 */
export function flattenBreaks(text: string): string {
  return text.replace(BREAKING_WS, ' ');
}

/** Greedy wrap of pre-measured words at a maximum line width. */
function wrapAt(
  words: string[],
  runWidth: (from: number, to: number) => number,
  maxWidth: number,
): number[] {
  // Break points as END indices (exclusive), one per line.
  const breaks: number[] = [];
  let start = 0;
  for (let index = 0; index < words.length; index++) {
    // A single word wider than the line still gets its own line: there is
    // nothing else to do with it, and a hyphenator is a different app.
    if (index > start && runWidth(start, index + 1) > maxWidth) {
      breaks.push(index);
      start = index;
    }
  }
  breaks.push(words.length);
  return breaks;
}

/**
 * Every way this label could reasonably be set: as one line, as two, as
 * three — each of them the NARROWEST block achieving that many lines, so
 * the block is as compact as it can be for the shape it is.
 *
 * Measured once per text, at edit time, and never again: the words'
 * widths do not change when the venn moves, and this is the whole reason
 * the live decision can be arithmetic.
 *
 * The candidate widths are exactly the widths of the contiguous runs of
 * words, because a greedy wrap can only ever break where a run stops
 * fitting — every other width gives one of the same answers. That makes
 * this exhaustive rather than a search, at k²/2 measurements for k words.
 */
export function typesetsFor(
  measure: (text: string) => number,
  metrics: FaceMetrics,
  text: string,
): Typeset[] {
  // THE TRAILING SPACE GOES MISSING HERE, AND IT IS MEASURABLE. Splitting
  // on whitespace and dropping the empties means "lovers " is set as
  // "lovers": the drawn line is centred on six glyphs. The editor's ghost
  // holds the real draft, trailing space and all, and the browser's
  // `pre-wrap` counts that space when it centres the line — so while the
  // last character typed is a space, the ghost's glyphs sit 1.2-1.9px to
  // the LEFT of the painted ones (measured, Dario 2026-08-24: -1.67px at
  // 28pt in a region, -1.90px at 22pt on a name, both cameras). The venn's
  // TITLE does not have this seam, and the contrast is the proof:
  // `titleFor` measures the whole string as one run, so it centres the
  // space too and the two agree.
  //
  // Left as it is, deliberately. Making the drawing count a trailing space
  // would shift painted words for a character nobody can see, which is a
  // worse bug wearing a fix's clothes; and there is no honest way to make
  // a platform text stack forget a character it has been given. Under two
  // pixels, only between two words, gone the moment the next letter lands.
  const words = text.split(BREAKING_RUN).filter((word) => word.length > 0);
  if (words.length === 0) return [];

  // Every contiguous run's width, measured through the real face so that
  // kerning across the spaces is in the number, not modelled around it.
  const widths: number[][] = [];
  for (let from = 0; from < words.length; from++) {
    widths.push([]);
    for (let to = from + 1; to <= words.length; to++) {
      widths[from]!.push(measure(words.slice(from, to).join(' ')));
    }
  }
  const runWidth = (from: number, to: number) => widths[from]![to - from - 1]!;

  const em =
    metrics.ascent + metrics.descent > 0
      ? metrics.ascent + metrics.descent
      : BASE_SIZE;
  const lineHeight = em * LINE_SPACING;

  // The narrowest block found so far for each line count.
  const best = new Map<number, number[]>();
  const candidates = new Set<number>();
  for (let from = 0; from < words.length; from++) {
    for (let to = from + 1; to <= words.length; to++) {
      candidates.add(runWidth(from, to));
    }
  }
  for (const width of candidates) {
    const breaks = wrapAt(words, runWidth, width);
    if (breaks.length > MAX_LINES) continue;
    let widest = 0;
    let start = 0;
    for (const end of breaks) {
      widest = Math.max(widest, runWidth(start, end));
      start = end;
    }
    const standing = best.get(breaks.length);
    if (standing === undefined || widest < standing[0]!) {
      best.set(breaks.length, [widest, ...breaks]);
    }
  }

  const typesets: Typeset[] = [];
  for (const count of [...best.keys()].sort((a, b) => a - b)) {
    const [widest, ...breaks] = best.get(count)!;
    const lines: Line[] = [];
    let start = 0;
    for (const end of breaks) {
      lines.push({
        text: words.slice(start, end).join(' '),
        width: runWidth(start, end),
      });
      start = end;
    }
    typesets.push({
      lines,
      blockW: widest!,
      blockH: lines.length * lineHeight,
      lineHeight,
      ascent: metrics.ascent,
      halfLeading: (lineHeight - em) / 2,
    });
  }
  return typesets;
}

/**
 * THE DRAWN BREAKS, WRITTEN INTO THE TEXT ITSELF — and this is the ghost's
 * whole defence against a second opinion about where the lines go.
 *
 * The invisible input used to be handed the raw words and a wrapping width,
 * and asked to arrive at the drawing's line breaking on its own. It cannot,
 * because it wraps with a different measurer. On iOS, measured on device
 * (Baloo 2 semibold at 23.80pt, the ghost's box 98.96pt wide): Skia and
 * CoreText agree on plain glyph runs to within 0.03pt — "lovers of" is
 * 89.24 to Skia and 89.21 to CoreText — but they do NOT agree about a
 * SPACE. Skia measures Baloo's space at 4.25pt where CoreText measures it
 * near 8.7pt, about twice as wide. So "the world", 96.04pt to Skia and
 * comfortably inside the box, is 100.86pt to CoreText and breaks. The
 * drawing painted three lines; the ghost laid out four, in a box only three
 * lines tall, and the selection slab a visitor saw sat above and beside
 * words that were no longer inside it.
 *
 * Slack cannot fix that, and this is the measured reason it cannot: the
 * disagreement per space (~4.5pt) is LARGER than a Skia space (4.25pt), so
 * any box wide enough to absorb it is wide enough for a word Skia pushed
 * onto the next line to climb back up. The two failures are on opposite
 * sides of a wall thinner than the error. There is no width that is right.
 *
 * So the ghost is not asked. The breaks the drawing chose are written into
 * the string the input holds, as newlines, and a hard break is a break no
 * measurer gets a vote on.
 *
 * ONE CHARACTER, IN PLACE, AND THAT IS THE POINT. Exactly one whitespace
 * character — the last of the run before a line's first word — becomes a
 * '\n'. Nothing is inserted and nothing is removed, so the string keeps its
 * LENGTH and every character keeps its INDEX. The caret offset, the
 * selection range, and the select-all-on-open all mean the same thing on
 * both sides of the substitution, which is what lets the input hold this
 * string while the document holds the visitor's own. Joining `line.text`
 * with newlines instead would have been shorter to write and would have
 * eaten a trailing space the moment one was typed — the next letter would
 * have landed against the previous word.
 *
 * Whitespace the visitor typed and the drawing ignores (a double space, a
 * pasted newline) survives here untouched, exactly as it did when the input
 * held the raw draft.
 */
export function breakAt(text: string, lines: readonly Line[]): string {
  // FLATTEN FIRST, ALWAYS — even when there is only one line and nothing to
  // break. This is the DRESSING path, and it is the second door into the
  // same wound: the keyboard path already flattens what a visitor types or
  // pastes, but a draft can also arrive from the DOCUMENT, and core caps a
  // label's length without saying one word about its whitespace. A venn
  // saved by an older build, or written by anything that is not this
  // editor's `onChangeText`, can carry a newline into a slot the drawing
  // paints on one line — and the ghost would lay out two, in a box built
  // for one. Closing only the clipboard door left the document door open.
  //
  // Length-preserving, one character for one, so every caret offset in the
  // editor still means what it means in the document.
  const flat = text.replace(BREAKING_WS, ' ').split('');
  if (lines.length >= 2) {
    // WORDS ARE RUNS OF NON-BREAKING CHARACTERS, not runs of `\S`. `\S`
    // treats NBSP as a word boundary, which is the opposite of what NBSP
    // means; `WORD_RUN` agrees with the tokenizer that produced `lines`,
    // and the two MUST agree or the boundary count below indexes a
    // different set of words than the drawing counted.
    const starts: number[] = [];
    WORD_RUN.lastIndex = 0;
    let found: RegExpExecArray | null;
    while ((found = WORD_RUN.exec(text)) !== null) starts.push(found.index);

    // CODE UNITS, BECAUSE THE REGEX COUNTS IN CODE UNITS. `exec().index` is
    // a UTF-16 offset; `[...text]` iterates CODE POINTS and collapses each
    // astral character to one element. Index the second with offsets from
    // the first and every emoji before a boundary drags the newline one
    // place left — into a word.
    //
    // Ferruccio found it unmutated on the branch, 2026-08-25, and it was
    // not a layout bug: the result goes back to the DOCUMENT, so
    // "😀 hey there you" came back as "😀 hey  here you" with the visitor's
    // own letter silently gone. A flag emoji pushed the boundary past the
    // last word and no break survived at all, which brought the floating
    // slab back with it.
    //
    // Writing a unit array is safe in this one place: `at - 1` is only ever
    // written when `at > 0`, and a word run starting at `at` means the unit
    // before it is breaking whitespace — always BMP, never half of a
    // surrogate pair. So no pair can be split.
    let seen = 0;
    for (const line of lines.slice(0, -1)) {
      seen += line.text.split(' ').length;
      const at = starts[seen];
      // A boundary that does not land on a word, or lands on the very first
      // character, has no whitespace in front of it to spend.
      //
      // SAID PLAINLY: neither branch is reachable from a typeset this file
      // produces, and neither changes the ANSWER if reached — `out[NaN]`
      // and `out[-1]` are properties `join` does not visit. Both mutations
      // were tried and no test could kill them. A statement of intent, not
      // a guard against an observed failure.
      if (at === undefined || at === 0) continue;
      flat[at - 1] = '\n';
    }
  }
  return flat.join('');
}

/**
 * The ghost's own lines — the strings the input ACTUALLY holds, one per
 * platform line, which is not the same list as the drawing's.
 *
 * `Typeset.lines` are the PAINT's lines: words rejoined with single spaces,
 * every run of whitespace collapsed to one. The ghost holds the visitor's
 * own characters, so a draft with a double space keeps both, and a line the
 * drawing paints at one width is set by the platform at another. Quoting
 * the box from the paint's list and then filling it with these was
 * Severino's finding: same length, different faces, and the box came up
 * short exactly when the extra faces were wide ones.
 *
 * So the box is quoted from THIS. Splitting on the newlines `breakAt` just
 * wrote is not an approximation of the ghost's line breaking — after
 * flattening there is no other break character left in the string, so it is
 * the whole of it.
 */
export function ghostLines(broken: string): string[] {
  return broken.split('\n');
}

/** The flat `[w, h, w, h, …]` table a worklet can read without objects,
 *  at the typesets' OWN measurement basis — the widths `measure` returned
 *  and the block heights `typesetsFor` computed from the face it was
 *  handed. The drawing uses this: it measured through the live face, so
 *  the face's own answers are the ones it wants. */
export function extentsOf(typesets: readonly Typeset[]): number[] {
  const flat: number[] = [];
  for (const typeset of typesets) flat.push(typeset.blockW, typeset.blockH);
  return flat;
}

/**
 * The same table, RE-SCALED to a caller's own type scale.
 *
 * `Typeset.blockH` is `lines * (ascent + descent) * LINE_SPACING` — the
 * FACE's designed line box, which for Baloo 2 is 1.602 em (a box sized for
 * Devanagari) and much taller than Latin ink ever reaches. The drawing is
 * happy with that: it is fitting words a hand is actively resizing and can
 * afford to be generous. A still picture cannot — it has one chance to use
 * the room, and its leading should be the leading it actually paints.
 *
 * So `render.ts` sets its own `lineRatio` against measured ink and builds
 * its table here. Widths are unchanged in shape and only rescaled from the
 * basis they were measured at (`from`) to the caller's ceiling (`base`);
 * heights are recomputed from the LINE COUNT, which is the whole point —
 * the same breakings, judged with the caller's own leading.
 *
 * This exists so there is exactly ONE implementation of "which breaking
 * draws biggest here": both callers hand their table to `biggestFit`, and
 * neither writes a comparison loop of its own.
 *
 * PASS YOUR OWN `base`, not just any consistent one. `base` is a common
 * positive factor across candidates, so while `fitScale` is doing real
 * work it cannot move the argmax — but `fitScale` is `min(1, 1/reach)`,
 * and once a budget is roomy enough that several breakings all CAP at
 * `base` they tie, and the tie goes to the first, which is the fewest
 * lines. So the pick can differ between a base that caps and one that
 * does not. (An earlier draft of this paragraph claimed the argmax was
 * independent of `base` outright; the test written to pin that claim went
 * red on the third budget it tried. Both halves are pinned now, in
 * labelFit.test.ts's "a caller's own type scale".)
 */
export function extentsAt(
  typesets: readonly Typeset[],
  from: number,
  base: number,
  lineRatio: number,
): number[] {
  const flat: number[] = [];
  const widthScale = from > 0 ? base / from : 0;
  for (const typeset of typesets) {
    flat.push(
      typeset.blockW * widthScale,
      typeset.lines.length * base * lineRatio,
    );
  }
  return flat;
}

/**
 * How plainly a label at this drawn size can be read: 1 above the floor,
 * fading to nothing over the few points below it.
 */
export function floorFade(size: number): number {
  'worklet';
  return Math.min(
    1,
    Math.max(0, (size - FLOOR_SIZE + FADE_RANGE_PT) / FADE_RANGE_PT),
  );
}
